import React, { useState, useEffect } from 'react';
import { getTableCell } from '../TableSettings';
import FormSelect from '../../../components/dropdown/formSelect';
import { TableBodyCellColumnCenter } from '../../../constants/style/table.styled';
import clsx from 'clsx';
import { FormWrapper } from '../../../components/input-text/input.styled';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import moment from 'moment';
import { TableError } from '../../../types/common';
import useDynamicTable from '../hooks/useDynamicTable';
import { DATE_FORMAT_WITH_TIME } from '@packages/shared/constants';
import _ from 'lodash';

interface EditableCellProps {
  cell: any;
  value: any;
  row: {
    original: any;
    index: number;
  };
  column: {
    id: string;
    editableType?: string;
    options?: { label: string; value: string }[];
    editable?: boolean;
    center?: boolean;
  };
  isEditable: boolean;
  isRowEditable: boolean;
  onEdit: (rowIndex: number, columnId: string, value: any, defaultValueName?: string) => void;
  onRowEdit: (rowId: string) => void;
  errors?: TableError[];
}

const EditableCell: React.FC<EditableCellProps> = ({
  cell,
  value: initialValue,
  row,
  column: { id, editableType, options, center },
  isEditable,
  isRowEditable,
  onEdit,
  onRowEdit,
  errors,
}) => {
  const [value, setValue] = useState(initialValue);
  const { setErrors } = useDynamicTable();

  const clearError = () => {
    if (errors && errors.length > 0) {
      const newErrors = errors.filter(
        (error) =>
          !(
            error.fieldName === (cell?.column?.id || cell?.column.accessor) &&
            (error.sNo as any) == row.index
          )
      );
      setErrors(newErrors);
    }
  };

  // const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (e: any) => {
    setValue(e.target.value);
    onEdit(row.index, id, e.target.value);
    clearError();
  };

  const handleSelectChange = (value: any, defaultValueName?: string) => {
    setValue(value);
    onEdit(row.index, id, value, defaultValueName);
    clearError();
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // const handleChange = (
  //   e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  // ) => {
  //   setValue(e.target.value);
  // };

  // const handleBlur = () => {
  //   setIsEditing(false);
  //   if (value !== initialValue) {
  //     onEdit(index.toString(), id, value);
  //   }
  // };

  // const handleDoubleClick = () => {
  //   if (isEditable) {
  //     setIsEditing(true);
  //   }
  // };

  const handleDoubleClick = (e: any) => {
    if (isEditable) {
      const id = e.currentTarget.id;
      onRowEdit(row.index.toString());
      setTimeout(
        () =>
          requestAnimationFrame(() => {
            const target = document.getElementById(id);
            if (target) {
              target.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
              });
            }
          }),
        0
      );
    }
  };

  if (!isEditable) {
    return getTableCell(cell);
  }

  if (isRowEditable) {
    const errorMessage =
      errors &&
      errors.find((error: TableError) => {
        // const subLevel = `${row.index}`.split('.');
        // const rowIndex = subLevel.length > 1 ? subLevel[1] : row.index;
        return (
          error.fieldName === (cell?.column?.id || cell?.column.accessor) &&
          (error.sNo as any) == row.index
        );
      })?.error;

    switch (editableType) {
      case 'number':
        return (
          <td
            {...cell.getCellProps({
              style: {
                minWidth: cell.column.minWidth,
                width: cell.column.width,
              },
              className: clsx({ 'error-cell': errorMessage }),
            })}
            id={`${cell?.column?.id}-${cell?.row?.index}`}>
            <FormWrapper
              style={{
                minWidth: cell.column.minWidth,
                width: cell.column.width,
              }}
              className={clsx('mb-0')}>
              <input
                id={`${cell?.column?.id}-${cell?.row?.index}`}
                type='number'
                value={value}
                onChange={(e) => {
                  let value = e.target.value;
                  if (_.isEmpty(value)) {
                    value = null;
                  }
                  if (Number(value) < 0) {
                    return;
                  }
                  handleChange({
                    ...e,
                    target: { ...e?.target, value },
                  });
                }}
              />
            </FormWrapper>
            <ErrorMessage message={errorMessage} />
          </td>
        );
      case 'date': {
        const withTime = cell.column.isDateFieldWithTime;
        const finalFormat = withTime ? DATE_FORMAT_WITH_TIME : 'YYYY-MM-DD';
        return (
          <td
            {...cell.getCellProps({
              style: {
                minWidth: cell.column.minWidth,
                width: cell.column.width,
              },
              className: clsx({ 'error-cell': errorMessage }),
            })}
            id={`${cell?.column?.id}-${cell?.row?.index}`}>
            <FormWrapper
              style={{
                minWidth: cell.column.minWidth,
                width: cell.column.width,
              }}
              className={clsx('mb-0')}>
              <CustomDatePicker
                id={`${cell?.column?.id}-${cell?.row?.index}`}
                name={cell.column.id}
                placeholder={cell.column.placeholder}
                onChange={(value: Date) => {
                  const formattedDate = moment(value).format(finalFormat);
                  handleChange({
                    target: { value: formattedDate },
                  });
                }}
                arrValue={value}
                isFormik={false}
                parentClassName='mb-0'
                isDateFieldWithTime={cell.column.isDateFieldWithTime}
                // onBlur={() => props.onBlur(props.id, props.value)}
                fixOverflow
              />
              {/* <input type='number' value={value} onChange={handleChange} /> */}
            </FormWrapper>
            <ErrorMessage message={errorMessage} />
          </td>
        );
      }
      case 'dropdown':
        return (
          <select value={value} onChange={handleChange}>
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'textarea':
        return (
          <textarea
            id={`${cell?.column?.id}-${cell?.row?.index}`}
            value={value}
            onChange={handleChange}
          />
        );
      case 'asyncSelect':
        return (
          <AsyncSelectField
            row={row}
            cell={cell}
            state={{ value }}
            handleChange={handleSelectChange}
            errorMessage={errorMessage}
          />
        );
      default:
        return (
          <td
            {...cell.getCellProps({
              style: {
                minWidth: cell.column.minWidth,
                width: cell.column.width,
              },
              className: clsx({ 'error-cell': errorMessage }),
            })}
            id={`${cell?.column?.id}-${cell?.row?.index}`}>
            <FormWrapper
              className={clsx('mb-0')}
              style={{
                minWidth: cell.column.minWidth,
                width: cell.column.width,
              }}>
              <input id={`${cell?.column?.id}-${cell?.row?.index}`} type='text' value={value} onChange={handleChange} />
            </FormWrapper>
            <ErrorMessage message={errorMessage} />
          </td>
        );
    }
  }

  //   return <span onDoubleClick={handleDoubleClick}>{getTableCell(cell)}</span>;
  return getTableCell(cell, handleDoubleClick);
};

function AsyncSelectField({ row, cell, handleChange, errorMessage }: any) {
  const name = cell.column.id || cell.column.accessor;
  const fieldValue = row.original[cell.column?.defaultValueName];

  const handleSelectChange = (selectedOption: any) => {
    handleChange(selectedOption, cell.column?.defaultValueName);
  };

  // const errorMessage =
  //   errors &&
  //   errors.find((error: TableError) => {
  //     return (
  //       error.fieldName?.toLowerCase() === cell?.column?.id?.toLowerCase() && error.sNo == row.index
  //     );
  //   })?.error;

  return (
    <td
      id={`${cell.column.id}-${row.index}`}
      {...cell.getCellProps({
        style: {
          minWidth: cell.column.minWidth,
          width: cell.column.width,
        },
      })}
      className={clsx({ 'error-cell': errorMessage })}>
      <TableBodyCellColumnCenter alignItems='center'>
        <FormSelect
          id={`${cell.column.id}-${row.index}`}
          type='text'
          width={cell.column.width}
          containerClassName='mb-0'
          hasParentError={errorMessage}
          name={name}
          placeholder={cell.column?.placeholder}
          onChange={handleSelectChange}
          options={cell?.column?.data || []}
          values={fieldValue}
          arrValue={fieldValue}
          isClearable={cell.column?.isClearable ?? true}
          isFormik={false}
        />
      </TableBodyCellColumnCenter>
      <ErrorMessage message={errorMessage} />
    </td>
  );
}

function ErrorMessage(props: { message?: string }) {
  return (
    <div
      style={{
        textAlign: 'left',
      }}>
      <span className={clsx('error-message')}>{props?.message}</span>
    </div>
  );
}

export default EditableCell;
